import backgroundBenchmark from "@images/backgroundBenchmark.png"
import InvitationIcon from "@images/invitationIcon.svg"
import { ClockOutlineIcon } from "@images/svgIcons/clockOutlineIcon"
import { SwitchDeviceIcon } from "@images/svgIcons/illustrations/switchDeviceIcon"
import { CorrectIcon } from "@images/svgIcons/status/correctIcon"
import { ErrorIcon } from "@images/svgIcons/status/errorIcon"
import { AppleAppStoreIcon } from "@images/svgIcons/store/appleAppStore"
import { GooglePlayStoreIcon } from "@images/svgIcons/store/googlePlayStore"
import { UnionIcon } from "@images/svgIcons/unionIcon"
import { WarningIcon } from "@images/svgIcons/warningIcon"
import { useUser } from "@modules/auth/hooks/useUser"
import { useBox } from "@modules/box/useBox"
import { BoxOfferFormattedPrice } from "@modules/boxOffer/components/boxOfferFormSection"
import {
  BoxOfferInvitationLink,
  BoxOfferInvitationStatus,
  BoxOfferType,
  Price,
} from "@modules/boxOffer/entities/boxOfferTypes"
import { useBoxOfferByUser } from "@modules/boxOffer/hooks/useBoxOffer"
import { useBoxOfferInvitationByUser, useBoxOfferSubscription } from "@modules/boxOffer/hooks/useBoxOfferSubscription"
import { CouponType } from "@modules/common/couponObject/gateway/couponObjectApiDto"
import { word } from "@modules/core/utils/i18n"
import { formatCurrency } from "@modules/currency/currency"
import { FeatureKeys } from "@modules/featureToggle/featureToggleTypes"
import { useFeatureToggle } from "@modules/featureToggle/hooks/useFeatureToggle"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { UserCell } from "@modules/members/components/userCell"
import { MemberOfferInvitationError } from "@modules/members/memberOffers/components/memberOfferInvitationError"
import { MemberOfferInvitationLoading } from "@modules/members/memberOffers/components/memberOfferInvitationLoading"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { LocalStorageKeys } from "@modules/storage/localStorageKeys"
import { HUButton } from "@modules/ui/components/huButton"
import { HUTag } from "@modules/ui/components/huTag"
import { HUText } from "@modules/ui/components/huText"
import Line from "@modules/ui/components/line"
import dayjs from "dayjs"
import { Tag } from "lucide-react"
import { QRCodeSVG } from "qrcode.react"
import { Link } from "react-router-dom"
import { useTheme } from "styled-components"

type Props = {
  boxOfferInvitationLink: BoxOfferInvitationLink
  className?: string
} & React.ComponentProps<"div">

export function MemberOfferInvitation({ boxOfferInvitationLink, className, ...props }: Props) {
  const defaultClassName = "flex flex-column"
  const canSubscribeToOffers = useFeatureToggle(FeatureKeys.BoxOffersByUser)
  const theme = useTheme()
  const user = useUser()
  const language = useLanguage()
  const { data: boxOffer, isLoading: boxOfferLoading } = useBoxOfferByUser(
    boxOfferInvitationLink.boxId,
    boxOfferInvitationLink.boxOfferId,
  )
  const { result: box, isLoading: boxLoading } = useBox(boxOfferInvitationLink.boxId)
  const { hideSidebar } = useSidebar()
  const { refuseOfferInvitation } = useBoxOfferInvitationByUser()
  const { subscribeToOffer, isProcessing } = useBoxOfferSubscription()

  const priceOfferObject =
    boxOffer?.priceObject && boxOffer.invitation?.coupon
      ? ({
          value: boxOffer.invitation.getPriceAfterDicount(boxOffer.priceObject.value),
          currency: boxOffer.priceObject.currency,
        } as Price)
      : boxOffer?.priceObject
  const isLoading = boxOfferLoading || boxLoading

  if (isLoading) return <MemberOfferInvitationLoading />
  if (
    !user ||
    !boxOffer ||
    !box ||
    !boxOffer.invitation ||
    user.id !== boxOffer.invitation.invitee.id ||
    boxOffer.invitation.status === BoxOfferInvitationStatus.CANCELLED ||
    boxOffer.invitation.status === BoxOfferInvitationStatus.REFUSED
  )
    return <MemberOfferInvitationError />

  return (
    <div className={className ?? defaultClassName} {...props}>
      {/* HEADER */}
      <div
        className="relative flex h-15rem bg-neutral-200 border-round-bottom-3xl"
        style={{ marginTop: "-85px", marginRight: "-20px", marginLeft: "-20px" }}
      >
        <div className="flex flex-column justify-content-center align-items-center w-6 h-full mt-2 ml-1">
          <div
            className="bg-contain bg-no-repeat bg-center"
            style={{ backgroundImage: `url('${InvitationIcon} ')`, width: "60px", height: "60px" }}
          ></div>
          <HUText fontStyle="HM" color={theme.neutralColor900} className="mt-1">
            {box.name}
          </HUText>
          <HUText fontStyle="BS" color={theme.neutralColor700} className="mt-2">
            {word("member.offer.invitation.header.invitationBy")}
          </HUText>
          <UserCell member={box.author} subtitle={box.author.username} className="flex gap-2 mt-1" />
        </div>
        <div
          className="bg-cover bg-no-repeat bg-right h-full w-6"
          style={{
            background: `url('${backgroundBenchmark}')`,
            clipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
            WebkitClipPath: "polygon(43% 0, 100% 0, 100% 100%, 0% 100%)",
          }}
        ></div>
      </div>
      {/* CONTENT */}
      {box && boxOffer && (
        <div className="flex flex-column gap-4">
          {/* Text content */}
          <div className="flex flex-column w-full justify-content-center align-items-center text-center	mt-5">
            <HUText fontStyle="TL" color={theme.neutralColor700}>
              {word("member.offer.invitation.subHeader.content", {
                box: box.name ?? "",
              })}
            </HUText>
          </div>

          {/* Card offer content */}
          <div className="relative flex flex-column  p-4  bg-neutralColor-100 rounded-33 gap-2 overflow-hidden">
            <HUText className="flex" fontStyle="LM" color={theme.neutralColor900}>
              {word(`box.offer.form.types.${boxOffer.type}`)}
            </HUText>
            <HUText className="flex" fontStyle="HM" color={theme.neutralColor900}>
              {boxOffer.name}
            </HUText>
            <div className="flex gap-2">
              <BoxOfferFormattedPrice
                price={priceOfferObject as Price}
                wholePartStyle="DS"
                decimalPartStyle="HM"
                color={theme.neutralColor800}
              />
              <div className="flex flex-column justify-content-end gap-1">
                {boxOffer.priceObject !== null && boxOffer.invitation.coupon && (
                  <HUText className="flex line-through" fontStyle="LM" color={theme.neutralColor700}>
                    {formatCurrency(boxOffer.priceObject.value, boxOffer.priceObject.currency, language)}
                  </HUText>
                )}
                <HUText className="flex  pb-2" fontStyle="LL" color={theme.neutralColor700}>
                  {boxOffer.getBoxOfferTypeWord()}
                </HUText>
              </div>
            </div>
            {/* Card offer Discount */}
            {boxOffer.priceObject !== null && boxOffer.invitation.coupon && (
              <div className="flex flex-column gap-2">
                <div className="flex gap-1 align-items-center">
                  <Tag color={theme.neutralColor600} size={13} />
                  <HUText className="flex" fontStyle="LS" color={theme.neutralColor600}>
                    {word("box.member.table.offer.item.discount.label")}
                  </HUText>
                </div>
                <div className="flex gap-1 align-items-center">
                  <HUText className="flex" fontStyle="LS" color={theme.neutralColor600}>
                    {boxOffer.invitation.coupon.type === CouponType.PERCENT
                      ? `-${boxOffer.invitation.coupon.value}%`
                      : `-${formatCurrency(boxOffer.invitation.coupon.value, boxOffer.priceObject.currency, language)}`}
                  </HUText>
                  <HUText className="flex" fontStyle="LS" color={theme.neutralColor600}>
                    ·
                  </HUText>
                  <HUText className="flex" fontStyle="LS" color={theme.neutralColor600}>
                    {boxOffer.invitation.coupon.durationInMonths
                      ? `${boxOffer.invitation.coupon.durationInMonths}
                    ${word("global.month.label", { count: boxOffer.invitation.coupon.durationInMonths ?? 0 })}`
                      : word("box.member.table.offer.discount.unlimitedInputLabel")}
                  </HUText>
                </div>
              </div>
            )}
            <UnionIcon
              color={theme.neutralColor400}
              className="absolute top-0 right-0  bg-no-repeat bg-center bg-contain w-full h-full"
              style={{ background: `url(${UnionIcon})`, left: "11rem" }}
            />
          </div>

          {/* Device change content */}
          {canSubscribeToOffers ? (
            <div className="flex justify-content-between flex-row w-full mt-4 gap-3">
              <HUButton
                type="Right_Icon"
                size="S"
                colorType="Quaternary"
                text={word("invitationDetailsInfo.refuse")}
                icon={{ icon: () => <ErrorIcon color={theme.mainWhite} width={20} height={20} /> }}
                onClick={() =>
                  boxOffer.invitation && refuseOfferInvitation.mutate({ boxOfferInvitationId: boxOffer.invitation.id })
                }
                disabled={refuseOfferInvitation.isPending}
                loading={refuseOfferInvitation.isPending}
                className="w-full"
              />
              <HUButton
                type="Right_Icon"
                size="S"
                colorType="Quaternary"
                text={word("invitationDetailsInfo.answerLater")}
                icon={{ icon: () => <ClockOutlineIcon color={theme.neutralColor900} width={14} height={14} /> }}
                className="w-full"
                onClick={() => {
                  hideSidebar()
                  localStorage.removeItem(LocalStorageKeys.sharedCoachInvitation.programId)
                }}
                disabled={isProcessing}
              />
              <HUButton
                type="Right_Icon"
                size="S"
                colorType="Primary"
                text={word("invitationDetailsInfo.accept")}
                icon={{ icon: () => <CorrectIcon color={theme.mainWhite} width={20} height={20} /> }}
                className="w-full"
                onClick={() =>
                  subscribeToOffer(boxOfferInvitationLink.boxId, boxOffer.id, boxOffer.priceObject?.value === 0)
                }
                disabled={isProcessing}
              />
            </div>
          ) : (
            <div className="relative flex flex-column  p-4 bg-neutralColor-100 rounded-33 gap-2 overflow-hidden">
              <div className="flex justify-content-center align-items-center gap-2">
                <WarningIcon color={theme.neutralColor800} width={24} height={24} />
                <HUText fontStyle="TL" color={theme.neutralColor800}>
                  {word("member.offer.invitation.deviceChange.title")}
                </HUText>
              </div>
              <HUText fontStyle="BM" color={theme.neutralColor900} className="flex justify-content-center">
                {word("member.offer.invitation.deviceChange.description")}
              </HUText>
              <div className="flex flex-row justify-content-center align-items-center gap-4">
                <HUText fontStyle="BM" color={theme.neutralColor900} className="flex justify-content-center w-6">
                  {word("member.offer.invitation.deviceChange.text")}
                </HUText>
                <SwitchDeviceIcon color={""} />
              </div>
              <div className="flex justify-content-center  align-items-center gap-4">
                <div className="flex flex-column gap-2">
                  <Link to={"https://play.google.com/store/apps/details?id=com.healthtech.hustleup"}>
                    <GooglePlayStoreIcon color="" />
                  </Link>
                  <Link to={"https://apps.apple.com/cm/app/hustle-up/id6450982235"}>
                    <AppleAppStoreIcon color="" />
                  </Link>
                </div>
                <div className="flex">
                  <QRCodeSVG value={boxOfferInvitationLink.shortLink} size={100} />
                </div>
              </div>
            </div>
          )}

          {/* Card detail content */}
          <div className="relative flex flex-column  mb-6 p-4  bg-neutralColor-100 rounded-33 gap-2 overflow-hidden">
            <HUText
              className="flex pb-3 border-bottom-1 border-neutralColor-200"
              fontStyle="TM"
              color={theme.neutralColor800}
            >
              {word("box.offer.form.infos.title")}
            </HUText>
            {/* Start date */}
            {boxOffer.invitation.deferredDate !== null && (
              <div className="flex flex-column  gap-2 py-3 border-bottom-1 border-neutralColor-200">
                <HUText fontStyle="TS" color={theme.neutralColor900}>
                  {word("box.offer.send.modal.startDate.title")}
                </HUText>
                <div className="flex align-items-baseline gap-1">
                  <HUText fontStyle="LL" color={theme.neutralColor900}>
                    {dayjs(boxOffer.invitation.deferredDate).format("L")}
                  </HUText>
                  <HUText fontStyle="LM" color={theme.neutralColor500}>
                    {word("box.offer.send.modal.startDate.inDays", {
                      count: dayjs(boxOffer.invitation.deferredDate).diff(dayjs().startOf("day"), "day"),
                    })}
                  </HUText>
                </div>
              </div>
            )}
            {/* Description */}
            <HUText
              fontStyle="BM"
              color={theme.neutralColor900}
              className="pt-3"
              numberOfLine={2}
              style={{ whiteSpace: "break-spaces", overflowWrap: "break-word" }}
              readMore={boxOffer.description.length > 180 ? true : false}
            >
              {boxOffer.description}
            </HUText>
            <Line color={theme.neutralColor200} strokeWidth={1} className="m-0 mt-3" />
            {/* Rules */}
            {boxOffer.type !== BoxOfferType.DropIn && (
              <div className="flex flex-column gap-2 py-3 border-bottom-1 border-neutralColor-200">
                <HUText color={theme.neutralColor900} fontStyle="TS" className="flex">
                  {word("box.offer.form.infos.restrictedPeriod.title")}
                </HUText>
                <div className="flex justify-content-between gap-5">
                  <div className="flex flex-wrap gap-2 w-full">
                    {boxOffer.rules && boxOffer.rules.length > 0 ? (
                      boxOffer.rules.map((rule, index) => (
                        <div key={index} className="flex padding-card rounded-card border-1 border-neutralColor-300">
                          <HUText className="w-full text-center" fontStyle="TS">{`${rule.value} ${word(
                            "offer.form.rules.sessions",
                            { count: rule.value },
                          )} ${word(`offer.form.rules.period.per.${rule.period}`)}`}</HUText>
                        </div>
                      ))
                    ) : (
                      <div className="flex w-full padding-card rounded-card border-1 border-neutralColor-300">
                        <HUText className="w-full text-center" fontStyle="TS">
                          {word("box.offer.form.infos.rules.unlimited")}
                        </HUText>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Activity included */}
            <div className="flex flex-column gap-2 border-bottom-1 border-neutralColor-200 py-3">
              <HUText color={theme.neutralColor900} fontStyle="TS">
                {word("box.offer.form.infos.activitiesIncluded.title")}
              </HUText>
              <div className="flex flex-wrap gap-2 mt-2">
                {boxOffer.programs.map((program, index) => (
                  <HUTag key={index} className="bg-neutralColor-300">
                    <HUText fontStyle="LM" color={theme.mainBlack}>
                      {program.name}
                    </HUText>
                  </HUTag>
                ))}
              </div>
            </div>

            {/* Commitment period */}
            {boxOffer.minimumCommitmentPeriod && (
              <div className="flex flex-column gap-2  py-3">
                <HUText color={theme.neutralColor900} fontStyle="TS">
                  {word("box.offer.form.minimum_monthly_duration.label")}
                </HUText>
                <div className="flex justify-content-between gap-2">
                  <HUText color={theme.neutralColor900} fontStyle="BM">
                    {word("member.offer.invitation.minimumCommitmentPeriod.text")}
                  </HUText>
                  <HUText color={theme.neutralColor900} fontStyle="BM">
                    {boxOffer.minimumCommitmentPeriod}{" "}
                    {word("global.month.label", { count: parseInt(boxOffer.minimumCommitmentPeriod, 10) })}
                  </HUText>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

// http://localhost:1234/BOX_OFFER_INVITATION?shortLink=https://hustleupdev.page.link/cesbrYxaZbHN1Pb56&boxId=7c0e73f784a64ee6a4cafc5c6f390cd4&boxOfferId=fd6fd15cad49439baba956997a490a03
